/**
 * ID Number formatting utility
 * Formats ID numbers to the required format: "901101 0000 00 0"
 * Validates month (01-12) and day (01-31) ranges
 */

/**
 * Formats an ID number to the required format with spaces
 * @param {string} idNumber - The ID number to format
 * @returns {string} - Formatted ID number
 */
export function formatIdNumber(idNumber) {
  if (!idNumber) return '';
  
  // Remove all non-digit characters
  const digits = idNumber.replace(/\D/g, '');
  
  // If less than 13 digits, return as is
  if (digits.length < 13) return idNumber;
  
  // Format as YYMMDD 0000 00 0
  if (digits.length >= 13) {
    return `${digits.slice(0, 6)} ${digits.slice(6, 10)} ${digits.slice(10, 12)} ${digits.slice(12, 13)}`;
  }
  
  return idNumber;
}

/**
 * Validates if an ID number is in the correct format with strict validation
 * @param {string} idNumber - The ID number to validate
 * @returns {boolean} - True if valid format
 */
export function validateIdFormat(idNumber) {
  if (!idNumber) return false;
  
  // Remove all non-digit characters
  const digits = idNumber.replace(/\D/g, '');
  
  // Must be exactly 13 digits
  if (digits.length !== 13) return false;
  
  // Must contain only digits
  if (!/^\d+$/.test(digits)) return false;
  
  // Extract date components
  const year = parseInt(digits.slice(0, 2));
  const month = parseInt(digits.slice(2, 4));
  const day = parseInt(digits.slice(4, 6));
  const gender = parseInt(digits[6]);
  const sequence = parseInt(digits.slice(7, 10));
  const citizenship = parseInt(digits[10]);
  const race = parseInt(digits[11]);
  
  // Validate month
  if (month < 1 || month > 12) return false;
  
  // Validate day
  if (day < 1 || day > 31) return false;
  
  // Validate actual date (handles leap years, month lengths)
  if (!isValidDate(year, month, day)) return false;
  
  // Validate gender digit (must be 0-9)
  if (isNaN(gender) || gender < 0 || gender > 9) return false;
  
  // Validate sequence number (000-999)
  if (isNaN(sequence) || sequence < 0 || sequence > 999) return false;
  
  // Validate citizenship (must be 0 or 1)
  if (citizenship !== 0 && citizenship !== 1) return false;
  
  // Validate race digit (must be 0-9)
  if (isNaN(race) || race < 0 || race > 9) return false;
  
  // Validate check digit
  if (!validateCheckDigit(digits)) return false;
  
  // Validate year range
  const fullYear = year <= 29 ? 2000 + year : 1900 + year;
  if (fullYear < 1900 || fullYear > 2099) return false;
  
  return true;
}

/**
 * Formats ID number on input (for v-model)
 * @param {string} value - The input value
 * @returns {string} - Formatted value
 */
export function formatIdInput(value) {
  if (!value) return '';
  
  // Remove all non-digit characters
  const digits = value.replace(/\D/g, '');
  
  // Limit to 13 digits
  const limitedDigits = digits.slice(0, 13);
  
  // Format as user types
  if (limitedDigits.length <= 6) {
    return limitedDigits;
  } else if (limitedDigits.length <= 10) {
    return `${limitedDigits.slice(0, 6)} ${limitedDigits.slice(6)}`;
  } else if (limitedDigits.length <= 12) {
    return `${limitedDigits.slice(0, 6)} ${limitedDigits.slice(6, 10)} ${limitedDigits.slice(10)}`;
  } else {
    return `${limitedDigits.slice(0, 6)} ${limitedDigits.slice(6, 10)} ${limitedDigits.slice(10, 12)} ${limitedDigits.slice(12)}`;
  }
}

/**
 * Validates if a date is valid (handles leap years, month lengths)
 * @param {number} year - 2-digit year (00-99)
 * @param {number} month - Month (1-12)
 * @param {number} day - Day (1-31)
 * @returns {boolean} - True if valid date
 */
function isValidDate(year, month, day) {
  // Convert 2-digit year to 4-digit (assume 1900-2099)
  // Years 00-29 are 2000-2029, years 30-99 are 1930-1999
  const fullYear = year <= 29 ? 2000 + year : 1900 + year;
  
  // Validate month
  if (month < 1 || month > 12) return false;
  
  // Validate day
  if (day < 1 || day > 31) return false;
  
  // Create date object to validate (month is 0-indexed in JS Date)
  const date = new Date(fullYear, month - 1, day);
  
  // Check if date is valid (handles leap years, month lengths automatically)
  return (
    date.getFullYear() === fullYear &&
    date.getMonth() === month - 1 &&
    date.getDate() === day
  );
}

/**
 * Validates South African ID number check digit using standard Luhn algorithm
 * Algorithm: Process from RIGHT to LEFT, double every second digit from right
 * If doubling results in > 9, subtract 9 (equivalent to summing digits)
 * @param {string} digits - 13 digit ID number
 * @returns {boolean} - True if check digit is valid
 */
function validateCheckDigit(digits) {
  if (digits.length !== 13) return false;
  
  // Extract check digit (last digit)
  const checkDigit = parseInt(digits[12]);
  
  // Standard Luhn algorithm: Process from RIGHT to LEFT (excluding check digit)
  // Double every second digit from the right (positions 0, 2, 4, 6, 8, 10 from right)
  // If doubling results in > 9, subtract 9 (which is equivalent to summing digits)
  
  let sum = 0;
  
  // Process first 12 digits from RIGHT to LEFT (positions 11 down to 0)
  for (let i = 11; i >= 0; i--) {
    let digit = parseInt(digits[i]);
    
    // Position from right: 0 = rightmost, 1 = second from right, etc.
    const positionFromRight = 11 - i;
    
    // Even positions from right (0, 2, 4, 6, 8, 10) are doubled
    if (positionFromRight % 2 === 0) {
      digit *= 2;
      // If result > 9, subtract 9 (same as summing digits: 14-9=5, same as 1+4=5)
      if (digit > 9) {
        digit -= 9;
      }
    }
    // Odd positions from right are added directly
    
    sum += digit;
  }
  
  // Calculate check digit: The amount needed to round sum up to nearest multiple of 10
  // This is: (10 - (sum % 10)) % 10
  const calculatedCheck = (10 - (sum % 10)) % 10;
  
  return calculatedCheck === checkDigit;
}

/**
 * Gets ID number validation rules for Vuetify with strict validation
 * @returns {Array} - Array of validation rules
 */
export function getIdValidationRules() {
  return [
    v => !!v || 'ID number is required',
    v => {
      if (!v) return true; // Skip validation if empty (handled by required rule above)
      
      // Remove all non-digit characters
      const digits = v.replace(/\D/g, '');
      
      // Must be exactly 13 digits
      if (digits.length !== 13) {
        return 'ID number must be exactly 13 digits';
      }
      
      // Must contain only digits
      if (!/^\d+$/.test(digits)) {
        return 'ID number must contain only digits';
      }
      
      // Extract date components
      const year = parseInt(digits.slice(0, 2));
      const month = parseInt(digits.slice(2, 4));
      const day = parseInt(digits.slice(4, 6));
      const gender = parseInt(digits[6]);
      const sequence = parseInt(digits.slice(7, 10));
      const citizenship = parseInt(digits[10]);
      const race = parseInt(digits[11]);
      const checkDigit = parseInt(digits[12]);
      
      // Validate month range
      if (month < 1 || month > 12) {
        return 'Invalid month in date of birth (must be 01-12)';
      }
      
      // Validate day range (preliminary check)
      if (day < 1 || day > 31) {
        return 'Invalid day in date of birth (must be 01-31)';
      }
      
      // Validate actual date (handles leap years, month lengths)
      if (!isValidDate(year, month, day)) {
        return 'Invalid date of birth in ID number';
      }
      
      // Validate gender digit (must be 0-9)
      if (isNaN(gender) || gender < 0 || gender > 9) {
        return 'Invalid gender digit in ID number';
      }
      
      // Validate sequence number (000-999)
      if (isNaN(sequence) || sequence < 0 || sequence > 999) {
        return 'Invalid sequence number in ID number';
      }
      
      // Validate citizenship (must be 0 or 1)
      if (citizenship !== 0 && citizenship !== 1) {
        return 'Invalid citizenship digit (must be 0 or 1)';
      }
      
      // Validate race digit (must be 0-9, though this is historical)
      if (isNaN(race) || race < 0 || race > 9) {
        return 'Invalid race digit in ID number';
      }
      
      // Validate check digit using standard Luhn algorithm
      if (!validateCheckDigit(digits)) {
        return 'Invalid ID number - check digit validation failed';
      }
      
      // Additional check: ID numbers before 1900 or after 2099 are suspicious
      const fullYear = year <= 29 ? 2000 + year : 1900 + year;
      if (fullYear < 1900 || fullYear > 2099) {
        return 'Invalid birth year in ID number';
      }
      
      return true;
    }
  ];
}

/**
 * Validates month range (01-12)
 * @param {string} idNumber - The ID number to validate
 * @returns {boolean} - True if month is valid
 */
export function validateMonth(idNumber) {
  if (!idNumber) return false;
  
  const digits = idNumber.replace(/\D/g, '');
  if (digits.length < 4) return false;
  
  const month = parseInt(digits.slice(2, 4));
  return month >= 1 && month <= 12;
}

/**
 * Validates day range (01-31)
 * @param {string} idNumber - The ID number to validate
 * @returns {boolean} - True if day is valid
 */
export function validateDay(idNumber) {
  if (!idNumber) return false;
  
  const digits = idNumber.replace(/\D/g, '');
  if (digits.length < 6) return false;
  
  const day = parseInt(digits.slice(4, 6));
  return day >= 1 && day <= 31;
}
